import React, { useState } from "react";
import { X, Copy, Check, MessageCircle, Instagram, Twitter, Share2 } from "lucide-react";
import { toast } from "sonner";
import { ACCESSORY_LABELS } from "@/lib/petStats";

const APP_URL =
  typeof window !== "undefined" ? window.location.origin : "https://creep.app";

const buildText = (pet) => {
  const cosmeticNames = (pet.accessories || [])
    .map((a) => ACCESSORY_LABELS[a])
    .filter(Boolean);
  const cosmeticLine = cosmeticNames.length
    ? `Cosméticos: ${cosmeticNames.join(", ")}. `
    : "";
  return `🐾 Creep · Racha de ${pet.streak || 1} días esquiando en Pirineos con CREEP. ${cosmeticLine}Únete: ${APP_URL}`;
};

export const ShareStreak = ({ pet, trigger }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = buildText(pet);
  const encoded = encodeURIComponent(text);

  const handleTrigger = async () => {
    // Try native Web Share API first
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Creep · Susanoo",
          text,
          url: APP_URL,
        });
        return;
      } catch {
        /* user cancelled — fall back to modal */
      }
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copiado al portapapeles");
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const targets = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      color: "#25D366",
      href: `https://wa.me/?text=${encoded}`,
      testId: "share-whatsapp",
    },
    {
      key: "instagram",
      label: "Instagram",
      icon: Instagram,
      color: "#E1306C",
      // Instagram doesn't support prefilled sharing; suggest to open app + copy
      onClick: async () => {
        await copy();
        window.open("https://www.instagram.com/", "_blank", "noopener");
        toast("Texto copiado. Pégalo en tu Story.", { icon: <Instagram size={14} /> });
      },
      testId: "share-instagram",
    },
    {
      key: "twitter",
      label: "X / Twitter",
      icon: Twitter,
      color: "#8A6BFF",
      href: `https://twitter.com/intent/tweet?text=${encoded}`,
      testId: "share-twitter",
    },
  ];

  return (
    <>
      {/* Trigger — either the passed slot or default button */}
      {trigger ? (
        React.cloneElement(trigger, { onClick: handleTrigger })
      ) : (
        <button
          data-testid="share-streak-trigger"
          onClick={handleTrigger}
          className="creep-btn-ghost inline-flex items-center gap-2 text-xs !py-1.5 !px-3"
        >
          <Share2 size={14} /> Compartir racha
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}
          onClick={() => setOpen(false)}
          data-testid="share-streak-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5"
            style={{
              background: "linear-gradient(180deg, #14101F, #0B0B12)",
              border: "1px solid rgba(138,107,255,0.3)",
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#8A6BFF]">Compartir</div>
                <div className="font-extrabold text-lg">Tu racha con Creep</div>
              </div>
              <button
                data-testid="share-streak-close"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-white/70"
              >
                <X size={16} />
              </button>
            </div>

            {/* Preview card */}
            <div
              className="rounded-2xl p-4 mb-4"
              style={{
                background:
                  "radial-gradient(300px 200px at 80% 20%, rgba(91,46,255,0.4), transparent 65%), linear-gradient(180deg, #14101F, #0B0B12)",
                border: "1px solid rgba(138,107,255,0.35)",
              }}
              data-testid="share-preview"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl" style={{ background: "radial-gradient(closest-side, rgba(91,46,255,0.55), rgba(11,11,18,0.9))", border: "1px solid rgba(138,107,255,0.4)" }}>
                  <span className="text-white text-lg">🐾</span>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Creep · Susanoo</div>
                  <div className="text-xl font-extrabold leading-tight">Racha de {pet.streak || 1} días</div>
                </div>
              </div>
              <p className="mt-3 text-sm text-white/85 whitespace-pre-wrap">{text}</p>
            </div>

            {/* Targets */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {targets.map((t) => {
                const Icon = t.icon;
                const inner = (
                  <div
                    className="flex flex-col items-center justify-center gap-1.5 rounded-2xl py-3 border border-white/10 hover:border-white/25 hover:bg-white/5 active:scale-95 transition-all"
                  >
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ background: `${t.color}22`, boxShadow: `0 0 14px ${t.color}55` }}
                    >
                      <Icon size={18} style={{ color: t.color }} />
                    </span>
                    <span className="text-[11px] text-white/80">{t.label}</span>
                  </div>
                );
                return t.onClick ? (
                  <button key={t.key} data-testid={t.testId} onClick={t.onClick} className="text-left">
                    {inner}
                  </button>
                ) : (
                  <a
                    key={t.key}
                    data-testid={t.testId}
                    href={t.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-left"
                  >
                    {inner}
                  </a>
                );
              })}
            </div>

            <button
              data-testid="share-copy"
              onClick={copy}
              className="w-full flex items-center justify-center gap-2 rounded-2xl py-3 border border-white/10 hover:bg-white/5 text-sm"
            >
              {copied ? <Check size={16} className="text-emerald-300" /> : <Copy size={16} />}
              {copied ? "Copiado" : "Copiar texto"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareStreak;
